import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useApp } from '../context/AppContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RotateCcw, RotateCw, Crop as CropIcon } from 'lucide-react';
import { getRotatedImageSrc, getCroppedBlobFromImage } from '../lib/cropImage';

// Crop % centré par défaut (ou selon un ratio si fourni)
function defaultCrop(mediaW, mediaH, aspect) {
  if (aspect) {
    return centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaW, mediaH),
      mediaW,
      mediaH,
    );
  }
  return { unit: '%', x: 5, y: 5, width: 90, height: 90 };
}

const ASPECTS = [
  { key: 'free', label: 'Libre', value: undefined },
  { key: '1', label: '1:1', value: 1 },
  { key: '43', label: '4:3', value: 4 / 3 },
  { key: '34', label: '3:4', value: 3 / 4 },
  { key: '169', label: '16:9', value: 16 / 9 },
];

const PhotoEditor = ({ open, onClose, imageUrl, onSave, saving }) => {
  const { t } = useApp();
  const imgRef = useRef(null);

  const [rotation, setRotation] = useState(0);
  const [displaySrc, setDisplaySrc] = useState(imageUrl);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [aspect, setAspect] = useState(undefined);
  const [preparing, setPreparing] = useState(false);

  // Réinitialise à l'ouverture / changement de photo
  useEffect(() => {
    if (open) {
      setRotation(0);
      setAspect(undefined);
      setCrop(undefined);
      setCompletedCrop(null);
      setDisplaySrc(imageUrl);
    }
  }, [open, imageUrl]);

  // Recalcule la source affichée quand la rotation change
  useEffect(() => {
    let cancelled = false;
    if (!open || !imageUrl) return;
    (async () => {
      setPreparing(true);
      const src = await getRotatedImageSrc(imageUrl, rotation);
      if (!cancelled) {
        setDisplaySrc(src);
        setCrop(undefined);
        setCompletedCrop(null);
        setPreparing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rotation, imageUrl, open]);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    setCrop(defaultCrop(width, height, aspect));
  }, [aspect]);

  const applyAspect = (value) => {
    setAspect(value);
    const img = imgRef.current;
    if (img) setCrop(defaultCrop(img.width, img.height, value));
  };

  const rotate = (delta) => setRotation((r) => (((r + delta) % 360) + 360) % 360);

  const handleSave = async () => {
    const blob = await getCroppedBlobFromImage(imgRef.current, completedCrop);
    if (blob) onSave(blob);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl bezel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display uppercase tracking-[0.06em]">
            <CropIcon className="h-5 w-5 text-primary" /> {t('cabinets.editPhoto')}
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-muted-foreground -mt-2">{t('cabinets.cropHint')}</p>

        <div className="flex items-center justify-center w-full max-h-[52vh] overflow-auto bg-black/70 rounded-md p-2">
          {displaySrc && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspect}
              keepSelection
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={displaySrc}
                alt="Crop area"
                onLoad={onImageLoad}
                style={{ maxHeight: '48vh', maxWidth: '100%', display: 'block' }}
                crossOrigin="anonymous"
              />
            </ReactCrop>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4 pt-2">
          {/* Ratios */}
          <div className="flex items-center gap-2">
            <Label className="eyebrow text-[10px] text-muted-foreground">{t('cabinets.ratio')}</Label>
            <div className="flex gap-1">
              {ASPECTS.map((a) => (
                <Button
                  key={a.key}
                  type="button"
                  size="sm"
                  variant={aspect === a.value ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs font-mono"
                  onClick={() => applyAspect(a.value)}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Rotation */}
          <div className="flex items-center gap-2">
            <Label className="eyebrow text-[10px] text-muted-foreground">{t('cabinets.rotation')}</Label>
            <div className="flex gap-1">
              <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => rotate(-90)} title="-90°">
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" size="icon" className="h-7 w-7" onClick={() => rotate(90)} title="+90°">
                <RotateCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
          <Button
            className="font-display uppercase tracking-[0.14em] glow-amber"
            onClick={handleSave}
            disabled={saving || preparing || !completedCrop?.width}
          >
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhotoEditor;
